<!-- Glass-Orb des Chat-Widget-Launchers (chat-bubble.component.html,
     Variante `glass`) als wiederverwendbarer Avatar. Ebenenfolge und
     Geometrie sind unverändert übernommen; neu ist nur die Augen-Ebene,
     die bewusst INNERHALB des Clips und unter dem Glanz liegt, damit die
     Augen hinter Glas wirken statt aufgeklebt.

     Alle Farben kommen aus dem SCSS: `stop-color`, `stop-opacity`, `fill`
     und `stroke` sind echte CSS-Properties, deshalb tragen die Elemente
     hier nur Klassen. Als Präsentationsattribut ließe sich das nicht per
     Typ umschalten (Attribute lösen kein `var()` auf).

     `.agent-orb__shadow` + der `#actor`-Wrapper um das SVG sind für die
     Orbi-Ausdrücke (agent-orb-sequences.ts) da: Hüpfer animieren `actor`
     (translateY/scale) und `shadow` (scaleX/opacity) unabhängig vom SVG
     selbst, damit ein Hüpfer nicht die viewBox-Transforms von Spin/Scan
     überschreibt. Ruhezustand/Picker/Adoption-Modal binden nichts davon —
     ungenutzt bleiben sie unsichtbar bzw. ohne Effekt. -->
<!-- Speed Lines für `spin-centrifugal`, 1:1 aus der Lab-Bühne (index.html,
     `#spin-speed-lines-back/front`): die hintere Ebene liegt vor dem Orb im
     DOM und damit dahinter, die vordere danach und damit davor. Ohne sie las
     sich die Drehung auf 48–64px nur als "Augen rutschen weg" (Marcel,
     26.09.2026). Ruhend `opacity: 0`. -->
<svg class="agent-orb__speed" #speedBack viewBox="0 0 420 300" aria-hidden="true">
  <defs>
    <linearGradient [attr.id]="speedBackId" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stop-color="#7658ea" stop-opacity=".04" />
      <stop offset=".42" stop-color="#8166ef" stop-opacity=".7" />
      <stop offset="1" stop-color="#5140bb" stop-opacity=".08" />
    </linearGradient>
  </defs>
  <g [attr.stroke]="speedBackRef">
    <path class="agent-orb__speed-line agent-orb__speed-line--long" d="M49 99C128 62 292 62 371 99" />
    <path class="agent-orb__speed-line agent-orb__speed-line--medium" d="M24 123C113 86 307 86 396 123" />
    <path class="agent-orb__speed-line agent-orb__speed-line--fast" d="M7 148C108 110 312 110 413 148" />
    <path class="agent-orb__speed-line agent-orb__speed-line--medium" d="M22 172C113 138 307 138 398 172" />
  </g>
</svg>
<div class="agent-orb__shadow" #shadow aria-hidden="true"></div>
<div class="agent-orb__actor" #actor>
<!-- Pfeif-Noten für `idle-whistle` (agent-orb-sequences.ts) — HTML-Spans,
     keine SVG-Elemente: sie sitzen bewusst AUSSERHALB des Orb-Clip-Pfads
     (`clipRef`), damit sie über den Rand des Kreises hinaus aufsteigen
     können, statt an dessen Kante abgeschnitten zu werden. Ihre
     Basisposition ist eine feste `left/top`-Prozentangabe (Mundposition
     `cx=64 cy=82` im 20 20 88 88-viewBox ≈ 50%/70%); jedes Sequenz-
     Keyframe schreibt seine eigene volle `transform`-Kette inkl. des
     `translate(-50%, -50%)`-Zentrierungs-Präfixes, da Web-Animations-
     Keyframes den `transform`-Wert ersetzen, nicht mit dem CSS-Rest-Wert
     verketten. Ungenutzt (kein `idle-whistle` in `personalitySequences`)
     bleiben sie bei `opacity: 0` ohne sichtbaren Effekt. -->
<svg
  #orbSvg
  class="agent-orb__svg"
  xmlns="http://www.w3.org/2000/svg"
  viewBox="20 20 88 88"
  [style.width.px]="svgSize"
  [style.height.px]="svgSize"
  [attr.role]="label ? 'img' : null"
  [attr.aria-label]="label || null"
  [attr.aria-hidden]="label ? null : 'true'"
>
  <defs>
    <clipPath [attr.id]="clipId">
      <circle cx="64" cy="64" r="43" />
    </clipPath>

    <radialGradient [attr.id]="fillId" cx="35%" cy="23%" r="78%">
      <stop class="orb-fill-1" offset="0" />
      <stop class="orb-fill-2" offset=".25" />
      <stop class="orb-fill-3" offset=".56" />
      <stop class="orb-fill-4" offset="1" />
    </radialGradient>

    <radialGradient [attr.id]="glowId" cx="48%" cy="40%" r="58%">
      <stop class="orb-glow-1" offset=".58" />
      <stop class="orb-glow-2" offset=".82" />
      <stop class="orb-glow-3" offset="1" />
    </radialGradient>

    <linearGradient [attr.id]="bandAId" x1="8" y1="19" x2="119" y2="105">
      <stop class="orb-band-a-1" />
      <stop class="orb-band-a-2" offset=".38" />
      <stop class="orb-band-a-3" offset=".72" />
      <stop class="orb-band-a-4" offset="1" />
    </linearGradient>

    <linearGradient [attr.id]="bandBId" x1="7" y1="100" x2="118" y2="27">
      <stop class="orb-band-b-1" />
      <stop class="orb-band-b-2" offset=".36" />
      <stop class="orb-band-b-3" offset=".77" />
      <stop class="orb-band-b-4" offset="1" />
    </linearGradient>

    <linearGradient [attr.id]="rimId" x1="28" y1="28" x2="102" y2="104">
      <stop class="orb-rim-1" />
      <stop class="orb-rim-2" offset=".42" />
      <stop class="orb-rim-3" offset=".72" />
      <stop class="orb-rim-4" offset="1" />
    </linearGradient>

    <filter [attr.id]="blurId" x="-30%" y="-30%" width="160%" height="160%">
      <feGaussianBlur stdDeviation="8" />
    </filter>

    <!-- weicher Halo um die Augen, damit sie durchs Glas leuchten -->
    <filter [attr.id]="eyeGlowId" x="-90%" y="-90%" width="280%" height="280%">
      <feGaussianBlur stdDeviation="2.4" result="g" />
      <feMerge>
        <feMergeNode in="g" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>

  <g [attr.clip-path]="clipRef">
    <circle cx="64" cy="64" r="43" [attr.fill]="fillRef" />

    <g class="agent-orb__spin-a" #spinA [attr.filter]="blurRef" opacity=".94">
      <ellipse cx="60" cy="42" rx="60" ry="22" [attr.fill]="bandARef" transform="rotate(15 64 64)" />
      <ellipse cx="69" cy="82" rx="65" ry="25" [attr.fill]="bandBRef" transform="rotate(-12 64 64)" />
      <path
        fill="none"
        stroke="#fff"
        stroke-opacity=".38"
        stroke-width="10"
        stroke-linecap="round"
        d="M7 73c20-20 39-18 58-6 20 13 37 12 57-10"
      />
    </g>

    <g class="agent-orb__spin-b" #spinB [attr.filter]="blurRef" opacity=".72">
      <ellipse class="orb-solid-a" cx="43" cy="67" rx="57" ry="18" opacity=".62" transform="rotate(-31 64 64)" />
      <ellipse class="orb-solid-b" cx="85" cy="61" rx="55" ry="17" opacity=".54" transform="rotate(27 64 64)" />
      <path
        class="orb-wave"
        fill="none"
        stroke-opacity=".42"
        stroke-width="14"
        stroke-linecap="round"
        d="M-2 52c28 18 47 19 72 2 18-12 33-13 60-1"
      />
    </g>

    <circle class="agent-orb__glow" #glow cx="64" cy="64" r="42.5" [attr.fill]="glowRef" opacity=".48" />

    <!-- Augen: Scan- und Blink-Keyframes stammen aus dem KI-Tutor-Baustein,
         auf den Orb-Maßstab umgerechnet (Kopfbreite 280 → Orb 86, also
         Scan-Ausschlag 14 → 4). -->
    <g class="agent-orb__eyes" #eyesGroup [attr.filter]="eyeGlowRef">
      <g #openEyesGroup class="agent-orb__eyes-open-layer" [class.agent-orb__eyes-open]="showSmile">
        <g class="agent-orb__blink">
          <rect
            class="orb-eye"
            [attr.x]="eyes.x1" [attr.y]="eyes.y"
            [attr.width]="eyes.w" [attr.height]="eyes.h" [attr.rx]="eyes.r"
          />
          <rect
            class="orb-eye"
            [attr.x]="eyes.x2" [attr.y]="eyes.y"
            [attr.width]="eyes.w" [attr.height]="eyes.h" [attr.rx]="eyes.r"
          />
          <circle
            class="orb-eye-light"
            [attr.cx]="eyes.x1 + eyes.w / 2 - 1.5" [attr.cy]="eyes.lightY"
            [attr.r]="eyes.lightR" [attr.opacity]="eyes.lightOpacity"
          />
          <circle
            class="orb-eye-light"
            [attr.cx]="eyes.x2 + eyes.w / 2 - 1.5" [attr.cy]="eyes.lightY"
            [attr.r]="eyes.lightR" [attr.opacity]="eyes.lightOpacity"
          />
        </g>
      </g>

      <!-- Kein `*ngIf` mehr: die Lachbogen-Gruppe muss für JEDEN Typ im DOM
           stehen, damit die Orbi-Ausdrücke (laugh/hop-laugh) sie per
           ViewChild ansteuern können, auch wenn `eyes.smile` (nur Tutor)
           und `mood` (idle) beide false sind. `.agent-orb__eyes-smile`
           bleibt per SCSS-Default `opacity: 0` unsichtbar, solange nichts
           sie aktiv animiert — unverändertes Verhalten für alle bisherigen
           Einsatzorte. -->
      <g
        #smileEyesGroup
        class="agent-orb__eyes-smile orb-smile"
        fill="none"
        stroke-width="5"
        stroke-linecap="round"
      >
        <path [attr.d]="smilePathLeft" />
        <path [attr.d]="smilePathRight" />
      </g>
    </g>

    <!-- Pfeifmund — eigene Ebene, NEBEN statt IN `.agent-orb__eyes`: im Lab
         sitzt er in einer separaten `orbi__expression-layer`-Gruppe (siehe
         core/orbi.js). Als Kind von `#eyesGroup` hätte er jede Bewegung der
         Augen-Gruppe geerbt (Ambient-Scan, `idle-whistle`s eigene
         `eyes`-Keyframes) und wäre sichtbar mit nach oben gewandert, statt
         an seiner Mundposition zu bleiben (Marcel, 25.09.2026). Nur die
         geschlossene, pulsierende Fassung; die offene Ellipse aus dem Lab
         bleibt dort durchgehend unsichtbar (siehe agent-orb-sequences.ts)
         und wurde deshalb nicht mit übernommen. -->
    <circle
      #mouthWhistleClosed
      class="orb-mouth-whistle"
      cx="64" cy="82" r="4.2"
      opacity="0"
    />
  </g>

  <circle cx="64" cy="64" r="43" fill="none" [attr.stroke]="rimRef" stroke-width="1.1" />

  <g class="agent-orb__shine" #shine [attr.clip-path]="clipRef">
    <path
      fill="#fff"
      fill-opacity=".26"
      d="M33 34c17-12 45-13 66 2 9 7 12 15 8 23-18-19-46-26-77-14z"
      [attr.filter]="blurRef"
    />
    <ellipse
      cx="89" cy="49" rx="12" ry="20" fill="#fff" opacity=".32"
      transform="rotate(-45 89 49)"
      [attr.filter]="blurRef"
    />
  </g>
</svg>
<span class="agent-orb__note" #note0 aria-hidden="true">♪</span>
<span class="agent-orb__note" #note1 aria-hidden="true">♫</span>
<span class="agent-orb__note" #note2 aria-hidden="true">♪</span>
</div>
<svg class="agent-orb__speed" #speedFront viewBox="0 0 420 300" aria-hidden="true">
  <defs>
    <linearGradient [attr.id]="speedFrontId" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stop-color="#9a86ff" stop-opacity=".04" />
      <stop offset=".5" stop-color="#6f4af8" stop-opacity=".96" />
      <stop offset="1" stop-color="#4f3cc6" stop-opacity=".06" />
    </linearGradient>
  </defs>
  <g [attr.stroke]="speedFrontRef">
    <path class="agent-orb__speed-line agent-orb__speed-line--fast" d="M6 149C108 191 312 191 414 149" />
    <path class="agent-orb__speed-line agent-orb__speed-line--long" d="M21 174C113 215 307 215 399 174" />
    <path class="agent-orb__speed-line agent-orb__speed-line--medium" d="M47 199C130 237 290 237 373 199" />
    <path class="agent-orb__speed-line agent-orb__speed-line--short" d="M79 219C147 247 273 247 341 219" />
  </g>
</svg>
